define('bindingHandlers/radial',[
    'jquery',
    'lib/lodash4',
    'lib/knockout',
    'lib/d3',

    'extensions/i18n/knockout.i18n'
], function (
    $,
    _,
    ko,
    d3
) {
    'use strict';

    //
    // Constants
    //

    var ANIMATION_DURATION = 350;
    var EMPTY_RESULT = "-";

    var VALUE_TEXT_MARGIN = {
        bottom: 20
    };

    var TWO_PI = 2 * Math.PI;

    // Dom Data Key
    var ARC_SCALE_KEY = "radial-arc";
    var VALUE_KEY = "radial-value";

    //
    // Private Functions
    //

    /**
     * This function will execute the logic needed to animate the foreground's arc to its
     * new position.
     */
    var arcTween = function (transition, newAngle, arc) {
        transition.attrTween("d", function(datum) {
            var interpolate = d3.interpolate(datum.endAngle, newAngle);

            return function (t) {
                datum.endAngle = interpolate(t);
                return arc(datum);
            };
        });
    };

    /**
     * Check if the provided argument has a value (is not null or undefined).
     *
     * @return {Boolean}     Returns true if the provided argument is not null and is not
     *  undefined.
     */
    var hasValue = function (obj) {
        return !_.isUndefined(obj) && !_.isNull(obj);
    };

    /**
     * Render the radial's circles in the provided element
     *
     * @param  {DOM Element} element - The element to render the radial in.
     * @param  {d3.arc} arc - A d3 arc generator function
     * @param  {Decimal} startingValue - The starting value for the foreground's arc. This
     *  value should be between 0 and 1.
     */
    var renderRadial = function (element, arc, startingValue, bindingData) {
        var width = $(element).width();
        var height = $(element).height();

        var value = 0;
        var text = renderValueText(startingValue);

        if (hasValue(startingValue)) {
            value = startingValue;
        }

        var svg = d3.select(element)
                        .append("svg")
                            .attr("width", width)
                            .attr("height", height)
                            .append("g")
                                .attr("transform", "translate(" + width / 2 + "," + height / 2 + ")");

        var radial = svg.append("g")
                            .attr("class", "radial");

        // Create radial background
        radial.append("path")
                .datum({endAngle: TWO_PI})
                .attr("class", "radial-background")
                .attr("d", arc);

        // Create radial foreground
        radial.append("path")
                .datum({endAngle: value * TWO_PI})
                .attr("class", "radial-foreground")
                .attr("d", arc);

        var valueTextNode = radial.append("text")
                                .attr("class", "radial-value")
                                .text(text)
                                .attr("dy", function () {
                                    // vertically center the value text.
                                    return this.getBBox().height / 4;
                                });

        if (bindingData.titleMessageKey) {
            radial.append("text")
                    .attr("class", "radial-title")
                    .text(ko.i18n.renderString(bindingData.titleMessageKey))
                    .attr("dy", function () {
                        var valueTextRect =  valueTextNode.node().getBBox();
                        return valueTextRect.y + valueTextRect.height + VALUE_TEXT_MARGIN.bottom;
                    });
        }
    };

    var renderValueText = function (value) {
        if (hasValue(value)) {
            return ko.i18n.format(value, "p1");
        } else {
            return EMPTY_RESULT;
        }
    };

    //
    // Binding Definition
    //

    /**
     * Generate a radial chart that will display a single circular bar. The bar will
     * fill with color as the provided value changes. The provided value will be converted
     * into a percentage and be displayed as text in the center of the circle.
     *
     * Example usage:
     *
     * <div data-bind="radial: valueObservable"></div>
     *
     * @param {Float} value - a value betwee 0 and 1 that will be used to determine the
     *  text display in the radial and how much the bar should be filled.
     * @param {String} [titleMessageKey] A string representing a message key to be used
     *  as the title of the radial.
     */
    ko.bindingHandlers.radial = {
        init: function (element, valueAccessor) {
            var $element = $(element);
            var bindingData = ko.unwrap(valueAccessor());
            var value = _.isPlainObject(bindingData) ? ko.unwrap(bindingData.value) : bindingData;

            $element.addClass("radial");

            // Set up dimensions and values for the radial's arc
            var outerRadius = Math.min($element.width(), $element.height()) * 0.5;
            var innerRadius = outerRadius * 0.75;

            var arc = d3.svg.arc()
                .startAngle(0)
                .innerRadius(innerRadius)
                .outerRadius(outerRadius);

            // Render the arc

            renderRadial(element, arc, value, bindingData);

            // Save values that are required for the update function
            ko.utils.domData.set(element, ARC_SCALE_KEY, arc);
            ko.utils.domData.set(element, VALUE_KEY, value);
        },

        update: function (element, valueAccessor) {
            var bindingData = ko.unwrap(valueAccessor());
            var argValue = _.isPlainObject(bindingData) ? ko.unwrap(bindingData.value) : bindingData;

            var newValue = argValue;

            if (!hasValue(newValue)) {
                newValue = 0;
            }

            var oldValue =  ko.utils.domData.get(element, VALUE_KEY);

            if (!hasValue(oldValue)) {
                oldValue = 0;
            }

            var arc =  ko.utils.domData.get(element, ARC_SCALE_KEY);

            //
            // Animate radial to the new position based on the new value
            //
            d3.select(element).select(".radial-foreground")
                .transition()
                    .duration(ANIMATION_DURATION)
                    .call(arcTween, newValue * TWO_PI, arc);

            if (hasValue(argValue)) {
                d3.select(element).select(".radial-value")
                    .transition()
                        .duration(ANIMATION_DURATION)
                        .tween("value-text-tween", function () {
                            var interpolate = d3.interpolate(oldValue, newValue);

                            return function(t) {
                                this.textContent = renderValueText(interpolate(t));
                            };
                        });
            } else {
                d3.select(element).select(".radial-value")
                    .text(renderValueText(argValue));
            }


            // Save the new value to interpolate at next update
            ko.utils.domData.set(element, VALUE_KEY, newValue);
        }
    };
});

